import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { supabase, type BlogPost } from "@/lib/supabase";

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const { data, error } = await supabase
          .from("blogs")
          .select("*")
          .eq("published", true)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setPosts(data || []);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load posts");
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Website Safety Blog — Scam & Trust Guides</title>
        <meta name="description" content="Practical guides on how to check if a website is safe, spot online scams, protect yourself from phishing, and build trust as a website owner." />
        <meta property="og:title" content="Website Safety Blog — Transparency Badge" />
        <meta property="og:description" content="Guides on spotting scams, checking websites, and staying safe online." />
        <meta property="og:image" content="https://transparencybadge.com/og-image.png" />
        <meta property="og:url" content="https://transparencybadge.com/blog" />
        <link rel="canonical" href="https://transparencybadge.com/blog" />
      </Helmet>

      <section className="py-16 md:py-24">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Blog"
            title="Website Safety Guides & Articles"
            description="Practical, no-nonsense guides to help you stay safe online — whether you're checking a website or running one."
          />

          {loading && (
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
                  <div className="aspect-video bg-muted" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-20 rounded bg-muted" />
                    <div className="h-5 w-full rounded bg-muted" />
                    <div className="h-5 w-3/4 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-5/6 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-12 rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-destructive">
              Could not load posts: {error}
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="mt-12 text-center text-muted-foreground py-16">
              No posts published yet.
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
                  <Card className="h-full overflow-hidden transition-shadow hover:shadow-elevated">
                    <div className="aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <CardContent className="p-6">
                      <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                        {post.category}
                      </span>
                      <h2 className="mt-3 text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(post.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {post.author}
                        </span>
                      </div>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                        Read the full guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
